import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { Modal, notification, Tooltip, Typography } from 'antd'
import styled from 'styled-components'
import { colours, sizes } from '@qflow/theme'

import {
  SelectMeter,
  UTILITIES,
  UtilitySelector
} from 'portal/components/Utilities/common'
import { ColourButton, icons, Spacer, Spinner } from 'portal/lib/primitives'

import { trackMixpanel } from 'portal/store/mixpanel/actions'
import {
  addMeter,
  deleteMeter,
  editMeter
} from 'portal/store/utilities/meters/actions'

import { AddEditMeterModal } from './AddEditMeterModal'
import { Meter } from './Interface'
import { MeterPanel } from './MeterPanel'
import { useMetersLoader } from './useMetersLoader'

export const Meters = () => {
  const [selectedUtility, setSelectedUtility] = useState(UTILITIES.WATER)
  const [selectedMeterId, setSelectedMeterId] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [currentMeter, setCurrentMeter] = useState<Meter | null>(null)

  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()

  const { loading, error, reload, readOnly } = useMetersLoader()

  const dispatch = useDispatch()
  const { Title } = Typography

  const allMeters = useSelector((state: any) => state.meters)

  const EV_METER_ADD = 'frontend_utilities_meters_meter_added'
  const trackMeterAdd = useCallback(() => {
    dispatch(
      trackMixpanel(EV_METER_ADD, {
        project_id: projectId,
        account_id: accountId
      })
    )
  }, [accountId, dispatch, projectId])

  const EV_METER_EDIT = 'frontend_utilities_meters_meter_editted'
  const trackMeterEdit = useCallback(() => {
    dispatch(
      trackMixpanel(EV_METER_EDIT, {
        project_id: projectId,
        account_id: accountId
      })
    )
  }, [accountId, dispatch, projectId])

  const EV_METER_DELETE = 'frontend_utilities_meters_meter_deleted'
  const trackMeterDelete = useCallback(
    meterId => {
      dispatch(
        trackMixpanel(EV_METER_DELETE, {
          project_id: projectId,
          account_id: accountId,
          meter_id: meterId
        })
      )
    },
    [accountId, dispatch, projectId]
  )

  const handleUtilityChange = (utility: string) => {
    // Reset selected meter when utility changes
    setSelectedUtility(utility)
    setSelectedMeterId('')
  }

  const selectedMeter: Meter = useMemo(
    () =>
      allMeters.data && selectedMeterId
        ? allMeters.data.find(
            (meter: Meter) => meter.meterId === selectedMeterId
          )
        : null,
    [selectedMeterId, allMeters]
  )

  // Split meters into water and electricity
  const { waterMeters, electricityMeters } = useMemo(() => {
    let waterMeters: Meter[] = []
    let electricityMeters: Meter[] = []

    if (allMeters.data && allMeters.data.length > 0) {
      const tempMeters = [...allMeters.data]
      tempMeters.sort((a: Meter, b: Meter) => {
        return a.meterName.localeCompare(b.meterName)
      })

      waterMeters = tempMeters.filter(
        (meter: Meter) =>
          meter.utilityType.toLowerCase() === UTILITIES.WATER.toLowerCase()
      )
      electricityMeters = tempMeters.filter(
        (meter: Meter) =>
          meter.utilityType.toLowerCase() ===
          UTILITIES.ELECTRICITY.toLowerCase()
      )
    }

    return { waterMeters, electricityMeters }
  }, [allMeters.data])

  const handleEditMeter = (meter: Meter) => {
    setCurrentMeter(meter)
    setModalOpen(true)
  }

  const handleAddEditMeterSubmit = (
    meterName: string,
    meterReference: string,
    utilityType: string,
    showSuccessNotification: () => void,
    showFailureNotification: () => void
  ) => {
    if (!accountId || !projectId) {
      console.error('Account ID or Project ID is missing')
      return
    }

    if (currentMeter && currentMeter.meterId) {
      // Edit meter
      dispatch(
        editMeter(
          accountId,
          projectId,
          currentMeter.meterId,
          meterName,
          meterReference,
          utilityType,
          reload,
          showSuccessNotification,
          showFailureNotification
        )
      )
      trackMeterEdit()
    } else {
      // Add meter
      dispatch(
        addMeter(
          accountId,
          projectId,
          meterName,
          meterReference,
          utilityType,
          reload,
          showSuccessNotification,
          showFailureNotification
        )
      )
      trackMeterAdd()
      setSelectedMeterId('')
    }

    setCurrentMeter(null)
    setModalOpen(false)
  }

  const showDeleteSuccessNotification = () => {
    notification.success({
      message: 'Meter successfully deleted.',
      duration: 5,
      placement: 'top'
    })
  }

  const showDeleteFailureNotification = () => {
    notification.error({
      message: 'Meter failed to be deleted.',
      description: 'Please try again.',
      duration: 10,
      placement: 'top'
    })
  }

  const handleDeleteMeter = (meterId: string) => {
    Modal.confirm({
      title: 'Delete meter',
      content: 'This operation cannot be undone. Are you sure?',
      autoFocusButton: null,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        handleConfirmDelete(meterId)
      }
    })
  }

  const handleConfirmDelete = (meterId: string) => {
    if (!accountId || !projectId) {
      console.error('Account ID or Project ID is missing')
      return
    }
    dispatch(
      deleteMeter(
        accountId,
        projectId,
        meterId as string,
        reload,
        showDeleteSuccessNotification,
        showDeleteFailureNotification
      )
    )
    trackMeterDelete(meterId)
    setSelectedMeterId('')
  }

  return (
    <>
      {loading && <Spinner />}
      {error && <div>Error: {error.message}</div>}
      {!error && !loading && (
        <>
          <TitleBar>
            <LeftSide>
              <UtilitySelector
                selectedUtility={selectedUtility}
                onChange={handleUtilityChange}
              />
              <SelectMeter
                selectedMeterId={selectedMeterId}
                setSelectedMeterId={setSelectedMeterId}
                meters={
                  selectedUtility === UTILITIES.WATER
                    ? waterMeters
                    : electricityMeters
                }
              />
            </LeftSide>
            <Tooltip
              title={
                readOnly
                  ? 'You do not have permission to add or edit meters.'
                  : null
              }
              placement="left"
            >
              <div>
                <ColourButton.Action
                  id="NewMeter"
                  onClick={() => {
                    setCurrentMeter(null)
                    setModalOpen(true)
                  }}
                  disabled={readOnly}
                >
                  <icons.Plus size={sizes.SMALL} />
                  <Spacer.Fine />
                  New meter
                </ColourButton.Action>
              </div>
            </Tooltip>
          </TitleBar>
          <ScrollWrapper>
            {selectedUtility === UTILITIES.WATER && (
              <MetersSection>
                <Title level={4}>Water meters</Title>
                <MetersSubSection>
                  {selectedMeter && (
                    <MeterPanel
                      key={selectedMeter.meterId}
                      meter={selectedMeter}
                      readOnly={readOnly}
                      onEdit={() => handleEditMeter(selectedMeter)}
                    />
                  )}
                  {!selectedMeter &&
                    waterMeters.map(meter => (
                      <MeterPanel
                        key={meter.meterId}
                        meter={meter}
                        readOnly={readOnly}
                        onEdit={() => handleEditMeter(meter)}
                      />
                    ))}
                  {waterMeters.length === 0 && (
                    <NoMeters>
                      No water meters registered for this project
                    </NoMeters>
                  )}
                </MetersSubSection>
              </MetersSection>
            )}
            {selectedUtility === UTILITIES.ELECTRICITY && (
              <MetersSection>
                <Title level={4}>Electricity meters</Title>
                <MetersSubSection>
                  {selectedMeter && (
                    <MeterPanel
                      key={selectedMeter.meterId}
                      meter={selectedMeter}
                      readOnly={readOnly}
                      onEdit={() => handleEditMeter(selectedMeter)}
                    />
                  )}
                  {!selectedMeter &&
                    electricityMeters.map(meter => (
                      <MeterPanel
                        key={meter.meterId}
                        meter={meter}
                        readOnly={readOnly}
                        onEdit={() => handleEditMeter(meter)}
                      />
                    ))}
                  {electricityMeters.length === 0 && (
                    <NoMeters>
                      No electricity meters registered for this project
                    </NoMeters>
                  )}
                </MetersSubSection>
              </MetersSection>
            )}
          </ScrollWrapper>
          <AddEditMeterModal
            meter={currentMeter}
            isOpen={modalOpen}
            defaultUtilityType={selectedUtility}
            onCancel={() => setModalOpen(false)}
            onSubmit={handleAddEditMeterSubmit}
            onDelete={handleDeleteMeter}
          />
        </>
      )}
    </>
  )
}

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 1rem 0 0.75rem;
  margin: 0 0 1rem -0.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
  background-color: ${colours.WHITE};
  border-radius: 10px;

  h3 {
    margin: 0;
  }
`
const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`
const ScrollWrapper = styled.div`
  overflow-y: auto;
  height: calc(100vh - 160px);
  margin: 0 -0.75rem 0 -0.5rem;
  padding: 0 0.75rem 0 0.5rem;
`
const MetersSection = styled.div`
  padding: 1.25rem 1.25rem 1.5rem 1.375rem;
  margin: 0 0 1.25rem -0.5rem;
  box-shadow: 0px 1px 3px 0px #00000026;
  background-color: ${colours.WHITE};
  border-radius: 10px;

  h4 {
    font-size: 1.125rem;
    margin: 0 0 1rem;
  }
`

const MetersSubSection = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  gap: 1rem;
`
const NoMeters = styled.div`
  color: ${colours.MID_GREY};
`
